import React, { FC, useMemo } from 'react';
import {
  TypeAccountPageFields,
  TypeConditionalBannerFields,
  TypeLink_externalFields,
} from '@/core/generated-types';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import { HeaderText, UniversalCta } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  SUBSCRIPTION_STATUS,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import { Entry } from 'contentful';
import { isObjectEmpty } from '@/utils/helpers';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import { _getActionLinks } from '../../AccountSettingsPageTiles/components/ManageSubscriptionsTile/utils';
import useLocale from '@/hooks/useLocale';
import { TypeUserRecord } from '@/lib/api/accountManager/types';

enum SubscriptionConditions {
  ACTIVE = 'referral credits active subscription',
  CANCELLED = 'referral credits cancelled subscription',
  DEFAULT = 'referral credits without subscription',
}

interface ReferralBannerProps {
  banners: Entry<TypeConditionalBannerFields>[];
  user?: TypeUserRecord;
  subscriptions: TypeSubscriptionRecord[];
  testId?: string;
}
const ReferralBanners: FC<ReferralBannerProps> = ({
  banners,
  user,
  subscriptions = [],
  testId,
}) => {
  const locale = useLocale();

  // Filter active subscriptions and find the most recently created ones
  const latestActiveSubscription = useMemo(
    () =>
      subscriptions
        .filter(
          (item) => item.subscription_status === SUBSCRIPTION_STATUS.ACTIVE,
        )
        .reduce((mostRecent, subscription) => {
          if (isObjectEmpty(mostRecent)) return subscription;
          return new Date(subscription.created_at) >
            new Date(mostRecent.created_at)
            ? subscription
            : mostRecent;
        }, {} as TypeSubscriptionRecord),
    [],
  );

  // Filter cancelled subscriptions and find the most recently created ones
  const latestCancelledSubscription = useMemo(
    () =>
      subscriptions
        .filter(
          (item) => item.subscription_status === SUBSCRIPTION_STATUS.CANCELLED,
        )
        .reduce((mostRecent, subscription) => {
          if (isObjectEmpty(mostRecent)) return subscription;
          return new Date(subscription.created_at) >
            new Date(mostRecent.created_at)
            ? subscription
            : mostRecent;
        }, {} as TypeSubscriptionRecord),
    [subscriptions],
  );

  // Match the condition of referral banners to the priority of subscription statuses
  const banner = banners.find((banner) => {
    const { condition } = banner.fields;
    if (!isObjectEmpty(latestActiveSubscription)) {
      return condition === SubscriptionConditions.ACTIVE;
    } else if (!isObjectEmpty(latestCancelledSubscription)) {
      return condition === SubscriptionConditions.CANCELLED;
    } else {
      return condition === SubscriptionConditions.DEFAULT;
    }
  });

  // When none of the above conditions are met, the banner display condition
  // is probably wrong in Contentful, so the component won't render anything
  if (!banner) return null;

  // Finally, destructure the found reference banner fields and render the component
  const { title, description, backgroundColor, cta, condition } = banner.fields;

  // Override default CTA link URLs for existing customer portal subscriptions
  if (user) {
    if (condition === SubscriptionConditions.ACTIVE) {
      (cta as Entry<TypeLink_externalFields>).fields.linkUrl =
        constructLegacyCustomerPortalLink(locale, [
          user.uuid,
          'subscriptions',
          latestActiveSubscription.uuid,
          'prepay-extension',
        ]);
    } else if (condition === SubscriptionConditions.CANCELLED) {
      (cta as Entry<TypeLink_externalFields>).fields.linkUrl =
        constructLegacyCustomerPortalLink(locale, [
          user.uuid,
          'account-settings',
          'reactivate',
          latestCancelledSubscription.uuid,
        ]);
    }
  }

  return (
    <BackgroundColorWrapper color={backgroundColor?.fields.color}>
      <div
        className="my-10 flex flex-col items-start justify-center gap-1 rounded-md px-5 py-7 shadow-md lg:flex-row lg:items-center lg:gap-5 lg:px-10 lg:py-9"
        data-testid={testId || 'banner'}
      >
        <HeaderText
          text={title}
          size={HeaderSize.H3}
          align="left"
          classes="basis-1/4"
        />
        <p className="mb-2 basis-2/4 lg:mb-0 lg:text-lg">{description}</p>
        <UniversalCta link={cta} className="h-fit lg:ml-2 lg:mr-3" />
      </div>
    </BackgroundColorWrapper>
  );
};

export default React.memo(ReferralBanners);
